import React, { createContext, useContext, useState, useEffect } from 'react';

const HandicapContext = createContext();

export const useHandicap = () => {
    const context = useContext(HandicapContext);
    if (!context) {
        throw new Error('useHandicap must be used within a HandicapProvider');
    }
    return context;
};

export const HandicapProvider = ({ children }) => {
    // State for team handicap enabled status
    const [teamHandicapEnabled, setTeamHandicapEnabled] = useState(() => {
        try {
            const saved = localStorage.getItem('brownlow-handicap-enabled');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Error loading handicap enabled state from localStorage:', error);
            return {};
        }
    });

    // State for player handicap values
    const [playerHandicaps, setPlayerHandicaps] = useState(() => {
        try {
            const saved = localStorage.getItem('brownlow-handicap-values');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error('Error loading handicap values from localStorage:', error);
            return {};
        }
    });

    // State for temporary handicap input values (before blur)
    const [tempHandicapInputs, setTempHandicapInputs] = useState({});

    // Save to localStorage whenever state changes
    useEffect(() => {
        try {
            localStorage.setItem('brownlow-handicap-enabled', JSON.stringify(teamHandicapEnabled));
        } catch (error) {
            console.error('Error saving handicap enabled state to localStorage:', error);
        }
    }, [teamHandicapEnabled]);

    useEffect(() => {
        try {
            localStorage.setItem('brownlow-handicap-values', JSON.stringify(playerHandicaps));
        } catch (error) {
            console.error('Error saving handicap values to localStorage:', error);
        }
    }, [playerHandicaps]);

    // Toggle handicap enabled for a team
    const toggleTeamHandicap = (team) => {
        setTeamHandicapEnabled(prev => ({
            ...prev,
            [team]: !prev[team]
        }));
    };

    // Update temporary handicap input value
    const updateTempHandicapInput = (team, playerName, value) => {
        const key = `${team}-${playerName}`;
        setTempHandicapInputs(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Apply handicap value (on blur)
    const applyPlayerHandicap = (team, playerName, value) => {
        const key = `${team}-${playerName}`;
        
        // Parse and validate value (no negative values)
        const numValue = parseFloat(value);
        const finalValue = isNaN(numValue) ? 0 : Math.max(0, numValue);
        
        setPlayerHandicaps(prev => ({
            ...prev,
            [key]: finalValue
        }));

        // Clear temporary input
        setTempHandicapInputs(prev => {
            const newInputs = { ...prev };
            delete newInputs[key];
            return newInputs;
        });
    };

    // Get handicap value for a player
    const getPlayerHandicap = (team, playerName) => {
        const key = `${team}-${playerName}`;
        return playerHandicaps[key] || 0;
    };

    // Get temporary handicap input value
    const getTempHandicapInput = (team, playerName) => {
        const key = `${team}-${playerName}`;
        return tempHandicapInputs[key];
    };

    // Get display value for handicap input (temp value or actual value)
    const getDisplayHandicapValue = (team, playerName) => {
        const tempValue = getTempHandicapInput(team, playerName);
        if (tempValue !== undefined) {
            return tempValue;
        }
        const actualValue = getPlayerHandicap(team, playerName);
        return actualValue === 0 ? '' : actualValue.toString();
    };

    // Clear all handicaps
    const clearAllHandicaps = () => {
        setTeamHandicapEnabled({});
        setPlayerHandicaps({});
        setTempHandicapInputs({});
    };

    // Reset handicap values for a specific team (keeps handicap enabled)
    const resetTeamHandicaps = (team) => {
        setPlayerHandicaps(prev => {
            const newHandicaps = { ...prev };
            Object.keys(newHandicaps).forEach(key => {
                if (key.startsWith(`${team}-`)) {
                    delete newHandicaps[key];
                }
            });
            return newHandicaps;
        });

        setTempHandicapInputs(prev => {
            const newInputs = { ...prev };
            Object.keys(newInputs).forEach(key => {
                if (key.startsWith(`${team}-`)) {
                    delete newInputs[key];
                }
            });
            return newInputs;
        });
    };

    // Clear handicaps for a specific team (disables handicap and clears values)
    const clearTeamHandicaps = (team) => {
        setTeamHandicapEnabled(prev => {
            const newEnabled = { ...prev };
            delete newEnabled[team];
            return newEnabled;
        });

        resetTeamHandicaps(team);
    };

    const value = {
        teamHandicapEnabled,
        playerHandicaps,
        tempHandicapInputs,
        toggleTeamHandicap,
        updateTempHandicapInput,
        applyPlayerHandicap,
        getPlayerHandicap,
        getTempHandicapInput,
        getDisplayHandicapValue,
        clearAllHandicaps,
        clearTeamHandicaps,
        resetTeamHandicaps
    };

    return (
        <HandicapContext.Provider value={value}>
            {children}
        </HandicapContext.Provider>
    );
};
